/**
 * Public brochure page — customer opens this via SMS/email.
 *
 * Route:  /brochure/<token>   (SPA route, no auth required)
 * Fetches:  GET /api/public/brochure/<token>
 * Logs:     POST /api/public/brochure/<token>/event  {event_type}
 *
 * Layout (2026-09-14 redesign — feels like a brochure, not a VDP dump):
 *   dealer bar -> hero photo (+ secondary thumb strip) -> title/price/
 *   mileage -> templated pitch -> highlight callouts -> CTAs -> rep card
 *   -> full specs -> full features -> footer. Content unchanged from
 *   before; only ordering/hierarchy moved. Three CTAs stay functionally
 *   identical: tel:, sms: (pre-filled), and the dealer's credit-app link.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Phone, MessageCircle, Zap, Star, CheckCircle2, ExternalLink } from "lucide-react";
import { buildPitchAndHighlights, normalizeDrivetrain } from "./pitchEngine";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function getToken() {
  if (typeof window === "undefined") return null;
  const m = window.location.pathname.match(/^\/brochure\/([^/?#]+)/);
  return m ? m[1] : null;
}

async function logEvent(token, type) {
  try {
    await axios.post(`${API}/public/brochure/${token}/event`, { event_type: type });
  } catch (_e) { /* fire-and-forget */ }
}

function currency(n) {
  const v = Number(n);
  if (!v || Number.isNaN(v)) return "";
  return v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

export default function BrochurePage() {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [heroIdx, setHeroIdx] = useState(0);
  const [press, setPress] = useState(null);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setState({ loading: false, error: "Missing token.", data: null });
      return;
    }
    axios.get(`${API}/public/brochure/${token}`)
      .then((r) => setState({ loading: false, error: null, data: r.data }))
      .catch(() => setState({ loading: false, error: "Brochure not found.", data: null }));
  }, []);

  // Fetched separately from the main payload — a cache-miss on the press
  // lookup runs a live search + LLM extraction (a few seconds) and must
  // never block the customer's initial page render.
  useEffect(() => {
    if (!state.data) return;
    const token = getToken();
    axios.get(`${API}/public/brochure/${token}/press`)
      .then((r) => setPress(r.data?.press || null))
      .catch(() => setPress(null));
  }, [state.data]);

  if (state.loading) {
    return <div className="min-h-screen grid place-items-center text-gray-500 text-sm">Loading…</div>;
  }
  if (state.error || !state.data) {
    return (
      <div className="min-h-screen grid place-items-center p-6 text-center">
        <div>
          <div className="text-4xl mb-2">🚗</div>
          <div className="text-lg font-semibold text-gray-900">Brochure not available</div>
          <div className="text-sm text-gray-500 mt-1">
            The link may have expired. Reply to your sales rep to get a new one.
          </div>
        </div>
      </div>
    );
  }

  const { vehicle, dealer, rep, sms_prefill_body } = state.data;
  const token = getToken();
  const photos = Array.isArray(vehicle.photos) ? vehicle.photos.filter(Boolean) : [];
  const features = Array.isArray(vehicle.features) ? vehicle.features : [];
  const badges = Array.isArray(vehicle.badges) ? vehicle.badges : [];

  const title = vehicle.title || [vehicle.year, vehicle.make, vehicle.model, vehicle.trim]
                                    .filter(Boolean).join(" ");
  const brand = dealer.brand_color || "#1a1a2e";
  const accent = dealer.accent_color || "#e8a13a";

  const repFirst = (rep.name || "").split(" ")[0] || "us";
  const telHref = rep.phone ? `tel:${rep.phone.replace(/[^\d+]/g, "")}` : null;
  const smsHref = rep.phone
    ? `sms:${rep.phone.replace(/[^\d+]/g, "")}?&body=${encodeURIComponent(sms_prefill_body || "")}`
    : null;

  const { pitch, highlights } = buildPitchAndHighlights(vehicle);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="brochure-page"
         style={{ "--brand": brand, "--accent": accent }}>
      {/* Dealer header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        {dealer.logo_url
          ? <img src={dealer.logo_url} alt={dealer.name} className="h-9 w-auto max-w-[140px] object-contain" />
          : <div className="h-9 grid place-items-center px-3 font-bold text-white rounded"
                 style={{ background: brand }}>
              {String(dealer.name || "").slice(0, 2).toUpperCase()}
            </div>}
        <div className="text-[13px] text-gray-700 font-medium">{dealer.name}</div>
      </header>

      {/* Hero photo — one large image, thumbnails de-emphasized below it */}
      {photos.length > 0 && (
        <div className="bg-black" data-testid="brochure-hero-block">
          <div className="relative w-full h-[46vh] min-h-[260px] max-h-[520px] overflow-hidden">
            <img src={photos[heroIdx]} alt={title}
                 className="w-full h-full object-cover object-center"
                 loading="eager"
                 data-testid="brochure-hero-photo" />
          </div>
          {photos.length > 1 && (
            <div className="flex gap-1.5 px-3 py-1.5 overflow-x-auto bg-black/95"
                 data-testid="brochure-thumb-strip">
              {photos.map((p, i) => (
                <button key={p} onClick={() => setHeroIdx(i)}
                        className={`shrink-0 w-10 h-10 rounded overflow-hidden border transition-opacity ${
                          i === heroIdx ? "border-white opacity-100" : "border-white/20 opacity-45 hover:opacity-70"
                        }`}
                        data-testid={`brochure-thumb-${i}`}
                        aria-label={`Photo ${i + 1}`}>
                  <img src={p} alt="" loading="lazy" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Title + price + mileage */}
      <div className="px-4 pt-4 pb-3 bg-white">
        <h1 className="text-[22px] font-bold text-gray-900 leading-tight"
            data-testid="brochure-title">{title}</h1>
        <div className="mt-2 flex flex-wrap items-baseline gap-3">
          {vehicle.price ? (
            <span className="text-3xl font-bold" style={{ color: brand }} data-testid="brochure-price">
              {currency(vehicle.price)}
            </span>
          ) : null}
          {vehicle.mileage != null && (
            <span className="text-sm text-gray-500" data-testid="brochure-mileage">
              {Number(vehicle.mileage).toLocaleString()} mi
            </span>
          )}
        </div>
        {badges.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="brochure-badges">
            {badges.map((b) => (
              <span key={b} className="text-[11px] font-semibold px-2 py-1 rounded-full"
                    style={{ background: `${accent}22`, color: brand }}>{b}</span>
            ))}
          </div>
        )}
      </div>

      {/* Generated one-line pitch */}
      <div className="px-4 pb-4 bg-white border-b border-gray-200">
        <p className="text-[14.5px] text-gray-600 leading-relaxed italic"
           data-testid="brochure-pitch">
          {pitch}
        </p>
      </div>

      {/* Highlight callouts — best features only, 2x2 grid */}
      {highlights.length > 0 && (
        <section className="px-4 py-4 bg-white border-b border-gray-200"
                 data-testid="brochure-highlights">
          <div className="grid grid-cols-2 gap-2.5">
            {highlights.map(({ label, icon: Icon }, i) => (
              <div key={label + i}
                   className="flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5"
                   data-testid={`brochure-highlight-${i}`}>
                <div className="shrink-0 w-8 h-8 rounded-full grid place-items-center"
                     style={{ background: `${brand}14`, color: brand }}>
                  <Icon size={16} />
                </div>
                <span className="text-[12.5px] font-medium text-gray-800 leading-tight">{label}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* "What reviewers say" — model/trim-level press signal, not about
          this specific used unit. Cache-first, so this pops in a beat
          after the rest of the page on a cold cache; section is simply
          omitted (no placeholder) when no genuine positive signal exists. */}
      {press && (
        <section className="px-4 py-4 bg-white border-b border-gray-200"
                 data-testid="brochure-press">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
            What reviewers say about the {vehicle.trim || vehicle.model}
          </h2>
          {(press.nhtsa_overall_stars != null || press.mpg_city || press.mpg_highway ||
            press.owner_rating != null) && (
            <div className="flex flex-wrap gap-2 mb-3">
              {press.nhtsa_overall_stars != null && (
                <div className="flex items-center gap-1.5 rounded-lg bg-gray-50 border border-gray-100 px-3 py-2"
                     data-testid="brochure-press-nhtsa">
                  <div className="flex" aria-label={`${press.nhtsa_overall_stars} out of 5 stars`}>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} size={13}
                            className={n <= press.nhtsa_overall_stars ? "fill-current" : ""}
                            style={{ color: n <= press.nhtsa_overall_stars ? accent : "#d1d5db" }} />
                    ))}
                  </div>
                  <span className="text-[11px] text-gray-600 font-medium">NHTSA Safety</span>
                </div>
              )}
              {(press.mpg_city || press.mpg_highway) && (
                <div className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2"
                     data-testid="brochure-press-mpg">
                  <div className="text-sm font-bold text-gray-900">
                    {press.mpg_city || "—"}/{press.mpg_highway || "—"} MPG
                  </div>
                  <div className="text-[10px] text-gray-500">City/Highway</div>
                </div>
              )}
              {press.owner_rating != null && (
                <div className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2"
                     data-testid="brochure-press-owner-rating">
                  <div className="flex items-center gap-1">
                    <Star size={13} className="fill-current" style={{ color: accent }} />
                    <span className="text-sm font-bold text-gray-900">{press.owner_rating}</span>
                  </div>
                  <div className="text-[10px] text-gray-500">
                    {press.owner_rating_count ? `${press.owner_rating_count} owner reviews` : "Owner rating"}
                  </div>
                </div>
              )}
            </div>
          )}
          {press.pros?.length > 0 && (
            <ul className="space-y-1 mb-2" data-testid="brochure-press-pros">
              {press.pros.map((p, i) => (
                <li key={i} className="flex items-start gap-1.5 text-[13px] text-gray-700">
                  <CheckCircle2 size={14} className="shrink-0 mt-0.5" style={{ color: brand }} />
                  {p}
                </li>
              ))}
            </ul>
          )}
          {press.source_name && press.source_url && (
            <a href={press.source_url} target="_blank" rel="noreferrer"
               className="text-[11px] text-gray-400 underline inline-flex items-center gap-1"
               data-testid="brochure-press-source">
              via {press.source_name} <ExternalLink size={10} />
            </a>
          )}
        </section>
      )}

      {/* Three CTAs — moved up, right after the highlights */}
      <div className="px-4 py-4 grid grid-cols-1 gap-2 bg-white border-b border-gray-200"
           data-testid="brochure-cta-block">
        {telHref && (
          <a href={telHref} onClick={() => logEvent(token, "call_clicked")}
             className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-semibold text-white shadow-sm"
             style={{ background: brand }}
             data-testid="brochure-cta-call">
            <Phone size={16} /> Call {repFirst}
          </a>
        )}
        {smsHref && (
          <a href={smsHref} onClick={() => logEvent(token, "text_clicked")}
             className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-semibold border-2 bg-white"
             style={{ borderColor: brand, color: brand }}
             data-testid="brochure-cta-text">
            <MessageCircle size={16} /> Text {repFirst}
          </a>
        )}
        {dealer.credit_app_url && (
          <a href={dealer.credit_app_url} target="_blank" rel="noreferrer"
             onClick={() => logEvent(token, "credit_app_clicked")}
             className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-semibold text-white"
             style={{ background: accent }}
             data-testid="brochure-cta-credit-app">
            <Zap size={16} /> Get qualified for the best rates available
          </a>
        )}
      </div>

      {/* Rep contact card — moved up, right below the CTAs */}
      <section className="px-4 py-5 bg-white border-b border-gray-200"
               data-testid="brochure-rep-card">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
          Your sales specialist
        </h2>
        <div className="flex items-center gap-3">
          {rep.photo_url
            ? <img src={rep.photo_url} alt={rep.name}
                   className="w-14 h-14 rounded-full object-cover" />
            : <div className="w-14 h-14 rounded-full grid place-items-center font-bold text-white"
                   style={{ background: brand }}>
                {rep.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
              </div>}
          <div className="min-w-0">
            <div className="text-base font-semibold text-gray-900">{rep.name}</div>
            {rep.phone && <div className="text-sm text-gray-500">{rep.phone}</div>}
            {rep.email && <div className="text-xs text-gray-400 truncate">{rep.email}</div>}
          </div>
        </div>
      </section>

      {/* Full specifications — demoted, still complete */}
      <section className="px-4 py-5 bg-gray-50 border-b border-gray-200">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
          Specifications
        </h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm"
            data-testid="brochure-specs">
          {[
            ["Year", vehicle.year],
            ["Make", vehicle.make],
            ["Model", vehicle.model],
            ["Trim", vehicle.trim],
            ["Body", vehicle.body_style],
            ["Transmission", vehicle.transmission],
            ["Drivetrain", normalizeDrivetrain(vehicle.drivetrain)],
            ["Exterior", vehicle.exterior_color],
            ["Interior", vehicle.interior_color],
            ["VIN", vehicle.vin],
          ].filter(([, v]) => v).map(([k, v]) => (
            // Stacked label-over-value, not a horizontal flex pair — a
            // `justify-between` row with a long unbreakable value (e.g.
            // "Rear-Wheel Drive") has no min-width on mobile and overflows
            // into the neighboring grid column, visually overlapping it.
            <div key={k} className="border-b border-gray-100 pb-2">
              <dt className="text-gray-500 text-[11px] uppercase tracking-wider">{k}</dt>
              <dd className="text-gray-900 font-medium mt-0.5 break-words">{v}</dd>
            </div>
          ))}
        </dl>
      </section>

      {/* Full features checklist — demoted, still complete */}
      {features.length > 0 && (
        <section className="px-4 py-5 bg-white border-b border-gray-200">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
            Features
          </h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm"
              data-testid="brochure-features">
            {features.map((f) => (
              <li key={f} className="text-gray-700 py-1 border-b border-gray-200">✓ {f}</li>
            ))}
          </ul>
        </section>
      )}

      <footer className="text-center text-[11px] text-gray-400 py-6 px-4">
        Personalized brochure from {dealer.name} · {new Date().getFullYear()}
      </footer>
    </div>
  );
}
